// contentDeviceApi.ts
// 콘텐츠 이용 기기 관리 API (v1)
// - 채팅 전송 시점에 기기 슬롯을 확인/등록한다 (서비스별 3대)
// - 인증은 저장소의 다른 API와 동일하게 쿠키 방식을 따른다.
//   accessToken이 HttpOnly라 JS에서 읽을 수 없어 Authorization 헤더는 만들 수 없다.
//   accessToken / device_id 모두 domain=.dongascience.com 쿠키라
//   credentials: "include"만으로 서버에 전달된다.

const DEVICE_API_URL = process.env.REACT_APP_DEVICE_API_URL || "";

/**
 * 요청 타임아웃(ms)
 * 응답이 지연되면 채팅 전송 자체가 멈춘 것처럼 보이므로 대기 시간을 제한한다.
 * 타임아웃 시 AbortError로 실패하며, 호출부에서 채팅을 막지 않고 통과시킨다.
 */
const REQUEST_TIMEOUT_MS = 10000;

/** 진입 서비스 구분 (AIR: 과학동아 AiR, DL: d라이브러리) */
export type ContentServiceType = "AIR" | "DL";

/** 슬롯 초과 안내에 노출할 기존 등록 기기 */
export interface ActiveDevice {
  id: number;
  deviceName: string;
  registeredAt: string;
  lastUsedAt: string;
}

/** 기기 등록 / 교체 성공 응답 */
export interface RegisterDeviceResult {
  deviceRecordId: number;
  deviceName: string;
  lastUsedAt: string;
}

export const DEVICE_ERROR_CODE = {
  UNAUTHENTICATED: "UNAUTHENTICATED",
  SLOT_LIMIT_EXCEEDED: "SLOT_LIMIT_EXCEEDED",
  INVALID_DEVICE_NAME: "INVALID_DEVICE_NAME",
  DEVICE_NAME_TOO_LONG: "DEVICE_NAME_TOO_LONG",
  SERVICE_TYPE_MISMATCH: "SERVICE_TYPE_MISMATCH",
  DEVICE_NOT_OWNED: "DEVICE_NOT_OWNED",
  DEVICE_NOT_FOUND: "DEVICE_NOT_FOUND",
} as const;

export type DeviceErrorCode =
  (typeof DEVICE_ERROR_CODE)[keyof typeof DEVICE_ERROR_CODE];

/**
 * 기기 관리 API 에러
 * - code: 서버가 내려주는 에러 코드 (없으면 HTTP 상태 기반 추정)
 * - activeDevices/limit: SLOT_LIMIT_EXCEEDED(409)일 때만 채워짐
 */
export class ContentDeviceError extends Error {
  status: number;
  code: string;
  activeDevices: ActiveDevice[];
  limit: number | null;

  constructor(params: {
    status: number;
    code: string;
    message: string;
    activeDevices?: ActiveDevice[];
    limit?: number | null;
  }) {
    super(params.message);
    // ES5로 트랜스파일되는 환경에서도 instanceof가 동작하도록 프로토타입 복원
    Object.setPrototypeOf(this, ContentDeviceError.prototype);

    this.name = "ContentDeviceError";
    this.status = params.status;
    this.code = params.code;
    this.activeDevices = params.activeDevices || [];
    this.limit = params.limit ?? null;
  }

  /**
   * 슬롯 초과 여부
   * 서버가 에러 코드를 내려주지 않아도 제한이 무력화되지 않도록 상태 코드로도 판정한다.
   */
  get isSlotLimitExceeded(): boolean {
    return (
      this.status === 409 || this.code === DEVICE_ERROR_CODE.SLOT_LIMIT_EXCEEDED
    );
  }

  /** 인증 만료(401) 여부 */
  get isUnauthenticated(): boolean {
    return (
      this.status === 401 || this.code === DEVICE_ERROR_CODE.UNAUTHENTICATED
    );
  }

  /** 해제 대상을 찾을 수 없음 (다른 곳에서 이미 해제된 경우 포함) */
  get isDeviceNotFound(): boolean {
    return (
      this.status === 404 || this.code === DEVICE_ERROR_CODE.DEVICE_NOT_FOUND
    );
  }
}

/**
 * 응답 본문에서 데이터부를 꺼낸다.
 *
 * 개발서버 실제 응답은 { msg, status, payload } 형태로,
 * 스펙 문서의 { status, code, message, data } 와 다르다.
 * 스펙이 실제에 맞춰지거나 반대로 바뀔 수 있어 양쪽 모두 처리한다.
 */
const getPayload = (body: any): any => body?.payload ?? body?.data;

/** 실패 응답 본문을 ContentDeviceError로 변환 */
const toContentDeviceError = (status: number, body: any): ContentDeviceError => {
  const payload = getPayload(body);

  // 실제 응답은 에러 코드가 payload에 문자열로 들어온다
  // (예: { msg: "기기 정보가 없습니다.", status: 404, payload: "DEVICE_NOT_FOUND" })
  const code =
    (typeof payload === "string" ? payload : payload?.errorCode) ||
    body?.code ||
    `HTTP_${status}`;

  return new ContentDeviceError({
    status,
    code,
    message:
      body?.msg || body?.message || `기기 확인에 실패했습니다. (${status})`,
    activeDevices: Array.isArray(payload?.activeDevices)
      ? payload.activeDevices
      : [],
    limit: typeof payload?.limit === "number" ? payload.limit : null,
  });
};

/**
 * 기기 관리 API 공통 POST 요청
 *
 * @throws {ContentDeviceError} 서버가 오류를 반환한 경우
 */
const postDeviceApi = async <T>(path: string, body: object): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${DEVICE_API_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // 인증(accessToken)과 기기 식별(device_id) 모두 쿠키로 전달된다.
      // 두 쿠키 다 domain=.dongascience.com 이라 자동 첨부되므로 필수.
      credentials: "include",
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  // 오류 응답이 JSON이 아닌 평문일 수 있다 (예: 500 "시스템 오류가 발생하였습니다.")
  const parsed = await response.json().catch(() => null);

  if (!response.ok || parsed?.status === "ERROR") {
    throw toContentDeviceError(response.status, parsed);
  }

  return getPayload(parsed) as T;
};

/**
 * 채팅/뷰어 진입 직전 기기 등록
 *
 * - 이미 등록된 기기이거나 슬롯 여유가 있으면 200 (등록/재이용)
 * - 슬롯이 가득 찬 신규 기기면 409 SLOT_LIMIT_EXCEEDED
 */
export const registerContentDevice = (
  serviceType: ContentServiceType = "AIR"
): Promise<RegisterDeviceResult> =>
  postDeviceApi<RegisterDeviceResult>("/content-devices/register", {
    serviceType,
  });

/**
 * 슬롯 초과 시 교체 (기존 기기 해제 + 현재 기기 등록)
 *
 * register가 409를 반환했을 때 사용자가 해제 대상을 고르면 호출한다.
 * 서버에서 해제와 등록이 원자적으로 처리된다. (기획 STEP 04)
 *
 * @param revokeTargetId 해제할 기기 id (409 응답의 activeDevices[].id)
 */
export const replaceContentDevice = (
  revokeTargetId: number,
  serviceType: ContentServiceType = "AIR"
): Promise<RegisterDeviceResult> =>
  postDeviceApi<RegisterDeviceResult>("/content-devices/replace", {
    revokeTargetId,
    serviceType,
  });
